/**
 * Where the backdrop planet sits, in viewport pixels. The backdrop is anchored to the viewport, not
 * the stage box, so rail panes and the strip open over it without moving it (`NarrativeBackdrop`).
 * The frame is therefore laid out against the shell's fixed geometry: it centres the planet in the
 * stage that is left when a narrow rail page is open (strip + page on the left, COMMS on the right),
 * so the globe stays mostly visible in every pane state, and lets it rise from below the frame.
 */

/** The shell's fixed column widths (`shell.css`): collapsed strip, narrow rail page, COMMS. */
export const SHELL_COLUMNS = { strip: 68, page: 384, comms: 320 } as const;

export type PlanetFrame = {
  /** The planet's centre, in viewport pixels. */
  cx: number;
  cy: number;
  /** Its radius, in pixels. */
  r: number;
};

/**
 * A world's placement in that frame (`planetProfiles.ts`): its radius as a fraction of the default,
 * and its centre as a fraction of the way across the open stage.
 */
export type PlanetPlacement = { scale: number; x: number };

export const DEFAULT_PLANET_PLACEMENT: PlanetPlacement = { scale: 1, x: 0.46 };

export function planetFrame({ width, height }: { width: number; height: number }, placement = DEFAULT_PLANET_PLACEMENT): PlanetFrame {
  const left = SHELL_COLUMNS.strip + SHELL_COLUMNS.page;
  const right = width - SHELL_COLUMNS.comms;
  const span = Math.max(right - left, 240);
  const r = Math.max(120, Math.min(height * 0.5, span * 0.7)) * placement.scale;
  return { cx: left + span * placement.x, cy: height * 0.64, r };
}
